import React, { useEffect, useState } from 'react';
import { Typography, Divider, Grid, Slider, TextField, Button, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getOcplcConfiguration, saveOcplcConfiguration } from 'actions/ocplcAction';

const sectionMap = {
  DASHBOARD_DT: "Dashboard Data Transport Limits",
  PARAMETERS_DT: "Parameter Data Transport Limits",
  AVAILABLE_DT: "Localization Data Transport Limits",
  DEFAULT_LOC_DT: "Localization Data Transport Limits",
  MODEL_PAGE_SIZE: "Page Size Settings",
  MATERIAL_PAGE_SIZE: "Page Size Settings",
  SERVICE_PAGE_SIZE: "Page Size Settings",
  AVAILABLE_LOC_PAGE_SIZE: "Page Size Settings",
  DEFAULT_LOC_PAGE_SIZE: "Page Size Settings"
};

const minMap = {
  DASHBOARD_DT: 1,
  PARAMETERS_DT: 1,
  AVAILABLE_LOC_DT: 1,
  DEFAULT_LOC_DT: 1,
  MODEL_PAGE_SIZE: 9999,
  MATERIAL_PAGE_SIZE: 9999,
  SERVICE_PAGE_SIZE: 9999,
  AVAILABLE_LOC_PAGE_SIZE: 999,
  DEFAULT_LOC_PAGE_SIZE: 999
};

const OCPLCConfigurationScreen = ({ version, assignedRole, whoChanged }) => {
  const dispatch = useDispatch();
const ocplcConfigData = useSelector(state => state.ocplc.ocplcConfigurationData) || [];

  const [sections, setSections] = useState([]);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(getOcplcConfiguration(version));
  }, [dispatch, version]);

  useEffect(() => {
    if (ocplcConfigData.length > 0) {
      const sectionItems = {};
      const parameterValues = {};
      ocplcConfigData
        .filter(item => sectionMap[item.parameterKey])
        .forEach(item => {
          const header = sectionMap[item.parameterKey];
          if (!sectionItems[header]) sectionItems[header] = [];
          const min = minMap[item.parameterKey] || 1;
          const max = Number(item.parameterText) || 1;
          const value = Number(item.parameterValue) || min;
          sectionItems[header].push({ title: item.parameterKey, min, max, value });
          parameterValues[item.parameterKey] = value;
        });
      setSections(Object.entries(sectionItems).map(([header, items]) => ({ header, items })));
      setValues(parameterValues);
      setErrors({});
    }
  }, [ocplcConfigData]);

  const handleChange = (title, val, min, max) => {
  const value = Number(val);
  if (isNaN(value)) {
    setValues(prev => ({ ...prev, [title]: "" }));
    setErrors(prev => ({ ...prev, [title]: "Invalid number" }));
    return;
  }
  setValues(prev => ({ ...prev, [title]: value }));
  if (value < min) {
    setErrors(prev => ({ ...prev, [title]: `Minimum allowed is ${min}` }));
  } else if (value > max) {
    setErrors(prev => ({ ...prev, [title]: `Maximum allowed is ${max}` }));
  } else {
    setErrors(prev => ({ ...prev, [title]: undefined }));
  }
};

  const handleInput = (title, value, min, max) => {
  if (value === "") {
    setValues(prev => ({ ...prev, [title]: "" }));
    setErrors(prev => ({ ...prev, [title]: "Value is required" }));
    return;
  }

  const num = Number(value);

  if (isNaN(num)) {
    setValues(prev => ({ ...prev, [title]: value }));
    setErrors(prev => ({ ...prev, [title]: "Invalid number" }));
  } else {
    setValues(prev => ({ ...prev, [title]: num }));
    if (num < min) {
      setErrors(prev => ({ ...prev, [title]: `Minimum allowed is ${min}` }));
    } else if (num > max) {
      setErrors(prev => ({ ...prev, [title]: `Maximum allowed is ${max}` }));
    } else {
      setErrors(prev => ({ ...prev, [title]: undefined }));
    }
  }
};

  const handleReset = () => {
    const resetVals = {};
    sections.forEach(section =>
      section.items.forEach(item => {
        resetVals[item.title] = item.value;
      })
    );
    setValues(resetVals);
    setErrors({});
  };

  const handleSave = () => {
  const updateConfig = Object.entries(values).map(([parameterKey, parameterValue]) => ({
    parameterKey,
    parameterValue: String(parameterValue)
  }));

  const payload = {
    input: {
      UpdateConfiguration: updateConfig,
      whoChanged
    }
  };

  dispatch(saveOcplcConfiguration(payload));
};

  const lastModified = new Date().toLocaleString('en-GB', {
  timeZone: 'UTC',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true
});

   const disableSave =
    Object.values(values).some(val => val === "" || val === undefined) ||
    Object.values(errors).some(err => !!err);

  return (
    <Box sx={{ maxWidth: 900, m: '0 auto', p: 3, borderRadius: 5, background: '#fff', boxShadow: 2 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>OCPLC Configuration</Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Role: <span style={{ color: "#1976D2" }}>{assignedRole}</span>
        </Typography>
        <Divider sx={{ mt: 1, mb: 2 }} />
      </Box>
      {sections.map(section => (
        <div key={section.header}>
          <Typography variant="h5" sx={{ pt: 2, pb: 1 }}>{section.header}</Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2, pl: 2 }}>
            {section.items.map(item => (
              <>
                <Grid item xs={4} key={item.title + '-label'}>
                  <Typography>{item.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    min: {item.min}, max: {item.max}
                  </Typography>
                </Grid>
                <Grid item xs={5} key={item.title + '-slider'}>
                  <Slider
                    min={item.min}
                    max={item.max}
                    value={values[item.title] ?? item.min}
                    onChange={(_, val) => handleChange(item.title, val, item.min, item.max)}
                    step={1}
                  />
                </Grid>
                <Grid item xs={3} key={item.title + '-input'}>
                  {errors[item.title] && (
                    <Typography color="error" variant="caption">{errors[item.title]}</Typography>
                  )}
                  <TextField
                    value={values[item.title] ?? ""}
                    onChange={e => handleInput(item.title, e.target.value, item.min, item.max)}
                    variant="outlined"
                    fullWidth
                    type="number"
                    error={!!errors[item.title]}
                    inputProps={{
                      min: item.min,
                      max: item.max,
                      step: 1
                    }}
                    onKeyDown={e => {
                      if (e.key === '.' || e.key === 'e' || e.key === '-' || e.key === '+') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Grid>
              </>
            ))}
          </Grid>
          <Divider />
        </div>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Audit: &nbsp;
            <span style={{ fontWeight: 400 }}>
              WhoChanged: <span style={{ color: "#1976D2" }}>{whoChanged}</span>
              &nbsp;|&nbsp;
              LastModified: <span style={{ color: "#1976D2" }}>{lastModified}</span>
            </span>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant='outlined' color="primary" onClick={handleReset}>Cancel</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled = {disableSave}>Save</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default OCPLCConfigurationScreen;